import { motion } from "framer-motion";
import { ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from "recharts";
import { ArrowUpRight, Star } from "lucide-react";
import { PremiumStatusBadge } from "@/components/equinox/PremiumStatusBadge";

const metricTone = {
  blue: "from-blue-500/20 to-blue-300/10",
  violet: "from-white/16 to-blue-300/10",
  gold: "from-amber-300/18 to-yellow-200/8",
  emerald: "from-emerald-400/16 to-white/8",
  rose: "from-rose-400/16 to-white/8",
};

const metricAccent = {
  blue: "rgba(37,99,235,0.95)",
  violet: "rgba(255,255,255,0.88)",
  gold: "rgba(231,200,115,0.95)",
  emerald: "rgba(52,211,153,0.95)",
  rose: "rgba(251,113,133,0.95)",
};

const formatCompactReviews = (reviewCount) => {
  if (reviewCount >= 1000000) return `${(reviewCount / 1000000).toFixed(1)}M`;
  if (reviewCount >= 1000) return `${Math.round(reviewCount / 1000)}k`;
  return reviewCount.toString();
};

const trustLegend = [
  { label: "Live store data", variant: "live" },
  { label: "Estimated Equinox score", variant: "estimated" },
  { label: "Benchmark-based insight", variant: "benchmark" },
];

export const AsoReportDashboard = ({ report, reportRef, methodologyTrigger }) => {
  const dashboardCards = Object.values(report.dashboard_summary);
  const radarData = [
    { metric: "Rating", value: report.score_breakdown.rating_health },
    { metric: "Reviews", value: report.score_breakdown.review_volume },
    { metric: "Velocity", value: report.score_breakdown.review_velocity },
    { metric: "Keywords", value: report.score_breakdown.keyword_optimization },
    { metric: "Metadata", value: report.score_breakdown.metadata_optimization },
    { metric: "Screens", value: report.score_breakdown.screenshots_quality },
    { metric: "Competitors", value: report.score_breakdown.competitor_benchmark },
    { metric: "Sentiment", value: report.score_breakdown.sentiment_score },
  ];

  const competitorChartData = report.competitor_analysis.map((item) => ({
    name: item.name,
    rating: item.rating,
    reviews: Math.round(item.review_count / 1000),
  }));

  const appInitials = report.app_name
    ?.split(" ")
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  const storeLabel = report.store_type === "app_store" ? "App Store" : "Google Play";

  return (
    <section ref={reportRef} className="px-6 pb-6 pt-8 sm:px-8 lg:px-12" data-testid="aso-report-dashboard-section">
      <div className="mx-auto max-w-7xl space-y-8">
        <div className="noise-surface overflow-hidden rounded-[2.4rem] border border-white/[0.08] bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] p-6 shadow-[0_22px_90px_rgba(0,0,0,0.28)] lg:p-8" data-testid="aso-dashboard-hero-card">
          <div className="grid gap-6 lg:grid-cols-[0.82fr_1.18fr] lg:items-end">
            <div>
              <p className="section-kicker">AI ASO Dashboard</p>
              <h2 className="mt-5 max-w-[11ch] font-display text-4xl tracking-[-0.05em] text-white lg:text-5xl">
                {report.app_name} — full growth potential snapshot.
              </h2>
              <p className="mt-5 max-w-2xl text-sm leading-8 text-white/[0.62]">
                The dashboard below combines live store metadata with Equinox scoring heuristics to highlight the growth levers your team should act on now.
              </p>
              <div className="mt-5 flex flex-wrap gap-2" data-testid="aso-dashboard-trust-legend">
                {trustLegend.map((item) => (
                  <PremiumStatusBadge key={item.label} label={item.label} variant={item.variant} />
                ))}
              </div>
              <div className="mt-5" data-testid="aso-dashboard-methodology-trigger-wrap">
                {methodologyTrigger}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-[0.95fr_1.05fr]">
              <div className="control-room-panel rounded-[1.8rem] p-5" data-testid="aso-dashboard-app-summary-card">
                <div className="flex items-center gap-4">
                  {report.icon_url ? (
                    <img src={report.icon_url} alt={report.app_name} className="h-16 w-16 rounded-[1.2rem] object-cover" data-testid="aso-dashboard-app-icon" />
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center rounded-[1.2rem] border border-white/[0.08] bg-white/[0.04] font-display text-2xl text-white" data-testid="aso-dashboard-app-icon-fallback">
                      {appInitials}
                    </div>
                  )}

                  <div>
                    <p className="font-display text-2xl text-white" data-testid="aso-dashboard-app-name">{report.app_name}</p>
                    <div className="mt-2 flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.2em] text-white/[0.46]">
                      <span className="rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-1">{storeLabel}</span>
                      <span className="rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-1">{report.category}</span>
                      <PremiumStatusBadge label="Live" variant="live" />
                    </div>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-3 text-sm text-white/76">
                  <div className="control-room-panel rounded-[1.2rem] p-4">
                    <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.38]">Listing rating</p>
                    <p className="mt-2 text-lg text-white"><Star className="mr-2 inline h-4 w-4 text-amber-200" />{report.app_rating.toFixed(1)}</p>
                    <div className="mt-3"><PremiumStatusBadge label="Live store data" variant="live" /></div>
                  </div>
                  <div className="control-room-panel rounded-[1.2rem] p-4">
                    <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.38]">Review base</p>
                    <p className="mt-2 text-lg text-white">{formatCompactReviews(report.review_count)}</p>
                    <div className="mt-3"><PremiumStatusBadge label="Live store data" variant="live" /></div>
                  </div>
                </div>
              </div>

              <div className="control-room-panel rounded-[1.8rem] p-5" data-testid="aso-dashboard-opportunity-card">
                <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.38]">Opportunity lane</p>
                <div className="mt-3"><PremiumStatusBadge label="Estimated Equinox analysis" variant="estimated" /></div>
                <div className="mt-4 grid gap-3 sm:grid-cols-3 md:grid-cols-1 xl:grid-cols-3">
                  <div className="control-room-panel rounded-[1.2rem] p-4">
                    <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.38]">Lead score</p>
                    <p className="mt-2 font-display text-2xl text-white" data-testid="aso-dashboard-lead-score">{report.lead_score}</p>
                  </div>
                  <div className="control-room-panel rounded-[1.2rem] p-4">
                    <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.38]">Sentiment</p>
                    <p className="mt-2 font-display text-2xl text-white" data-testid="aso-dashboard-sentiment">{report.sentiment_label}</p>
                  </div>
                  <div className="control-room-panel rounded-[1.2rem] p-4">
                    <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.38]">Organic upside</p>
                    <p className="mt-2 font-display text-2xl text-white" data-testid="aso-dashboard-growth-potential">{report.growth_potential_percent}%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {dashboardCards.map((metric, index) => (
            <motion.div
              key={metric.label}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.45, delay: index * 0.06, ease: "easeOut" }}
              className="editorial-shell glitter-card control-room-panel rounded-[1.8rem] p-5"
              data-testid={`aso-dashboard-card-${index + 1}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.42]">{metric.label}</p>
                  <p className="mt-3 font-display text-4xl text-white">{Math.round(metric.value)}</p>
                  <div className="mt-3"><PremiumStatusBadge label="Estimated score" variant="estimated" /></div>
                </div>

                <div className="relative flex h-16 w-16 items-center justify-center rounded-full border border-white/[0.08] bg-black/20">
                  <div
                    className="absolute inset-1 rounded-full"
                    style={{
                      background: `conic-gradient(${metricAccent[metric.tone] || metricAccent.blue} ${metric.value * 3.6}deg, rgba(255,255,255,0.08) 0deg)`,
                    }}
                  />
                  <div className="absolute inset-[6px] rounded-full bg-[#060911]" />
                  <span className="relative font-display text-lg text-white">{Math.round(metric.value)}</span>
                </div>
              </div>

              <div className="mt-4 h-2 rounded-full bg-white/[0.08]">
                <motion.div
                  className={`h-full rounded-full bg-gradient-to-r ${metricTone[metric.tone] || metricTone.blue}`}
                  initial={{ width: 0 }}
                  whileInView={{ width: `${metric.value}%` }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.9, delay: 0.1 + index * 0.06, ease: "easeOut" }}
                />
              </div>
              <p className="mt-4 text-sm leading-7 text-white/[0.64]">{metric.insight}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-[0.88fr_1.12fr]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            className="glitter-card control-room-panel noise-surface rounded-[2rem] p-6"
            data-testid="aso-score-breakdown-chart-card"
          >
            <p className="section-kicker">Score breakdown</p>
            <h3 className="mt-4 font-display text-3xl text-white">Weighted ASO score formula</h3>
            <div className="mt-4" data-testid="aso-score-breakdown-source-label"><PremiumStatusBadge label="Estimated Equinox scoring model" variant="estimated" /></div>
            <div className="mt-5 flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.2em] text-white/[0.44]">
              {radarData.map((item) => (
                <span key={item.metric} className="rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-2">
                  {item.metric}: {item.value}
                </span>
              ))}
            </div>
            <div className="mt-6 h-[360px] min-w-0">
              <ResponsiveContainer width="100%" height="100%" minWidth={320} minHeight={320}>
                <RadarChart data={radarData} outerRadius="72%">
                  <PolarGrid stroke="rgba(255,255,255,0.08)" />
                  <PolarAngleAxis dataKey="metric" tick={{ fill: "rgba(255,255,255,0.58)", fontSize: 12 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: "rgba(255,255,255,0.32)", fontSize: 10 }} />
                  <Radar name="ASO score" dataKey="value" stroke="#DBEAFE" fill="#2563EB" fillOpacity={0.3} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.45, delay: 0.08, ease: "easeOut" }}
            className="glitter-card control-room-panel noise-surface rounded-[2rem] p-6"
            data-testid="aso-competitor-analysis-section"
          >
            <p className="section-kicker">Competitor Analysis</p>
            <h3 className="mt-4 font-display text-3xl text-white">Top 5 benchmark competitors</h3>
            <div className="mt-4" data-testid="aso-competitor-source-label"><PremiumStatusBadge label="Benchmark-based insight, not a live competitor API" variant="benchmark" /></div>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-white/[0.62]">
              Ratings and review depth are visualized together so you can see trust leadership and category pressure in one view.
            </p>
            <div className="mt-6 h-[320px] min-w-0">
              <ResponsiveContainer width="100%" height="100%" minWidth={320} minHeight={280}>
                <BarChart data={competitorChartData}>
                  <defs>
                    <linearGradient id="aso-rating-gradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#DBEAFE" />
                      <stop offset="100%" stopColor="#2563EB" />
                    </linearGradient>
                    <linearGradient id="aso-review-gradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FFFFFF" />
                      <stop offset="100%" stopColor="rgba(255,255,255,0.3)" />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="name" tick={{ fill: "rgba(255,255,255,0.58)", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="left" tick={{ fill: "rgba(255,255,255,0.42)", fontSize: 11 }} axisLine={false} tickLine={false} domain={[0, 5]} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: "rgba(255,255,255,0.42)", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#0a0c12", border: "1px solid rgba(255,255,255,0.08)", color: "#fff" }} />
                  <Bar yAxisId="left" dataKey="rating" fill="url(#aso-rating-gradient)" radius={[8, 8, 0, 0]} />
                  <Bar yAxisId="right" dataKey="reviews" fill="url(#aso-review-gradient)" radius={[8, 8, 0, 0]}>
                    {competitorChartData.map((item) => (
                      <Cell key={item.name} fill="url(#aso-review-gradient)" />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              {report.competitor_analysis.map((competitor, index) => (
                <div key={competitor.name} className="control-room-panel rounded-[1.5rem] p-4 transition-transform duration-300 hover:-translate-y-1" data-testid={`aso-competitor-card-${index + 1}`}>
                  <p className="font-display text-xl text-white">{competitor.name}</p>
                  <p className="mt-2 text-sm text-white/[0.72]">{competitor.rating.toFixed(1)}★ • {competitor.review_count.toLocaleString()} reviews</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <PremiumStatusBadge label="Benchmark" variant="benchmark" />
                    <span className="text-xs uppercase tracking-[0.2em] text-blue-100">{competitor.keyword_gap}</span>
                  </div>
                  <div className="mt-4 space-y-2 text-sm text-white/[0.62]">
                    <p><span className="text-white">Strengths:</span> {competitor.strengths.join(" • ")}</p>
                    <p><span className="text-white">Weaknesses:</span> {competitor.weaknesses.join(" • ")}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="glitter-card control-room-panel noise-surface rounded-[2rem] p-6"
          data-testid="aso-recommendations-section"
        >
          <p className="section-kicker">AI Recommendations</p>
          <h3 className="mt-4 font-display text-3xl text-white">Your highest-leverage fixes right now</h3>
          <div className="mt-4" data-testid="aso-recommendations-source-label"><PremiumStatusBadge label="Model-generated recommendations from live + estimated inputs" variant="estimated" /></div>
          <div className="mt-6 grid gap-4 lg:grid-cols-5">
            {report.recommendations.map((item, index) => (
              <div key={item.title} className="editorial-shell control-room-panel rounded-[1.6rem] p-5 transition-transform duration-300 hover:-translate-y-1" data-testid={`aso-recommendation-card-${index + 1}`}>
                <div className="flex items-center justify-between gap-3">
                  <PremiumStatusBadge label={item.priority} variant={item.priority === "Immediate" || item.priority === "High" ? "live" : "estimated"} />
                  <ArrowUpRight className="h-4 w-4 text-white/40" />
                </div>
                <p className="mt-3 font-display text-2xl text-white">{item.title}</p>
                <p className="mt-4 text-sm leading-7 text-white/[0.64]">{item.body}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};